import * as React from 'react';
import { makeStyles, createStyles, Typography, Box } from '@material-ui/core';
import cn from 'classnames';
import { getImageUrl } from '../../utils/imagesRoot';
import { TranquiTheme } from '../../containers/App/theme';
import { OutDebt } from '../../generated/graphql';
import { getEntityLabel } from '../../utils/financialEntitiesList';
import { getDebtTypeLabel } from '../../utils/getDebtTypeLabel';
import { getBankImage } from '../../utils/getBankImage';
import { transformInfoBank } from '../../containers/NegotiationDebts/transformInfoBank';
import { BankCardDynamicData } from './BankCardDynamicData';
import { getNegBooleans } from '../../utils/getNegBooleans';
import { useGetProductNumberDef } from '../../utils/useGetProductNumberDef';

export interface BankCardDynamicProps {
  helperMessage?: React.ReactNode,
  className?: string,
  debt: OutDebt,
  info: BankCardDynamicData[];
  children?: any;
}

const BankCardDynamic = (props:BankCardDynamicProps) => {
  const { debt, helperMessage, className, info, children } = props;
  const {
    isVirtualDebt, isRappiPay, isRappiPrestamo, isBogota, isJcap, isApex, isClaro,
  } = getNegBooleans(debt);
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const classes = useStyles();
  const { productNumber: productNumberRaw } = debt;
  const { bank, type } = transformInfoBank(debt);
  const bankImage = !isJcap && getBankImage(bank, isApex);

  const debtTypeLabel = getDebtTypeLabel(type, bank);
  const showProductNumber = !isRappiPay && !isRappiPrestamo && !isClaro;
  const productNumberDef = useGetProductNumberDef(debt);
  const showPaymentReference = isClaro && !!debt.extra?.paymentReference;
  const showProductBox = (!isVirtualDebt && (showProductNumber))
  || (isClaro && showPaymentReference);
  const productNumber = isBogota ? productNumberDef : `**** **** ${productNumberRaw}`;

  return (
    <>
      <Box
        className={cn(classes.root, isVirtualDebt && classes.rootACPK, className, helperMessage && 'mb-1')}
        data-test="cy-bankCard-box"
      >
        {bankImage ? (
          <Box className={classes.bankNameBox}>
            <img
              src={bankImage}
              alt={`Logo ${getEntityLabel(bank, (debt.brand as string) || undefined).toLowerCase()}`}
              data-test={`cy-logo-${bank}-bankCard`}
            />
          </Box>
        ) : null}
        {showProductBox && (
          <Box className={classes.productBox}>
            <Typography className={cn(classes.productType, 'mr-1')} data-test="cy-productType-bankCard">{debtTypeLabel}</Typography>
            {showProductNumber && <Typography className={classes.productType} data-test="cy-lastDigits-creditCard">{productNumber}</Typography>}
          </Box>
        )}

        {info.map(({ label, value, bold }, index) => (
          <Box key={`${label}${value}`}>
            <Typography className={classes.titleContent} data-test={`cy-${label.split(' ').join('')}-bankCard`}>{label}</Typography>
            <Typography
              className={cn(classes.textContentTitle, index >= 2 && classes.textContent)}
              data-testid={`bank-card-dynamic__${label.split(' ').join('')}-value-bankCard`}
              style={bold ? { fontWeight: 'bold' } : undefined}
            >
              {value}
            </Typography>
          </Box>
        ))}
        {children}
      </Box>
      {helperMessage}
    </>
  );
};

const useStyles = makeStyles((theme: TranquiTheme) => createStyles({
  root: {
    width: '100%',
    maxWidth: 450,
    minHeight: 280,
    boxShadow: theme.shadows[2],
    padding: theme.spacing(2),
    boxSizing: 'border-box',
    borderRadius: theme.spacing(0.5),
    display: 'grid',
    gridTemplate: 'repeat(4, auto) / 5fr 3fr',
    rowGap: `${theme.spacing(1)}px`,
    backgroundImage: `url(${getImageUrl('cards/cc_bg.png')})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    [theme.breakpoints.down('sm')]: {
      minHeight: 'auto',
    },
  },
  rootACPK: {
    gridTemplate: 'repeat(2, auto) / 3fr 4fr',
  },
  bankNameBox: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gridColumn: '1 / 3',
    '& img': {
      maxWidth: 200,
      maxHeight: 100,
      userSelect: 'none',
      pointerEvents: 'none',
    },
  },
  bankAvatar: {
    marginRight: theme.spacing(1),
    width: theme.spacing(3),
    height: theme.spacing(3),
  },
  bankName: {
    ...theme.typography.h5,
    color: theme.palette.indigo.main,
    fontWeight: 600,
  },
  productBox: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gridColumn: '1 / 3',
  },
  productType: {
    ...theme.typography.small as any,
    color: theme.palette.text3.main,
  },
  titleContent: {
    ...theme.typography.capitalize as any,
    color: theme.palette.text4.main,
    textTransform: 'uppercase',
    fontWeight: 500,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.8rem',
    },
  },
  textContentTitle: {
    ...theme.typography.body1,
    color: theme.palette.text1.main,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.9rem !important',
    },
  },
  textContent: {
    ...theme.typography.h4,
    color: theme.palette.text1.main,
  },
}));

export default BankCardDynamic;
